import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  BookmarkCheck,
  BookmarkPlus,
  Check,
  CheckCircle2,
  Download,
  Film,
  Heart,
  ImageOff,
  ImagePlus,
  Info,
  Loader2,
  Repeat2,
  RotateCcw,
  Trash2,
  Wand2,
  X,
} from "lucide-react";
import {
  buildDownloadFilename,
  cancelJob,
  CANCELLABLE_STATES,
  favoriteJob,
  isTerminalState,
  presignAsset,
  unfavoriteJob,
  type JobInfo,
  type JobState,
  type LibraryItemInfo,
} from "@/lib/api";
import { aspectBadgeLabel } from "@/lib/aspect";
import { JobDetailDialog } from "@/components/JobDetailDialog";
import { PriceApprovalControl } from "@/components/PriceApprovalControl";
import { formatUsd } from "@/lib/format";
import { SaveToLibraryDialog } from "@/components/SaveToLibraryDialog";
import type { JobPartial } from "@/hooks/useJobEvents";
import { useAssetIdUrl, useAssetUrl } from "@/hooks/useAssetUrl";
import { useRecreateStore } from "@/store/recreate";
import { cn } from "@/lib/utils";
import { useDownloadAsset } from "@/hooks/useDownloadAsset";
import { EmptyState } from "@/components/ui/empty-state";
import { AssetMedia } from "@/components/media/AssetMedia";
import { isSelectable } from "@/lib/selection";
import { neighborIndex, viewerItems } from "@/lib/viewerNav";
import type { GalleryCardSize } from "@/lib/galleryDensity";

/** Live SSE overlay state for the single in-progress job (see useJobEvents). */
export interface JobLiveState {
  pct?: number;
  message?: string;
  partials: JobPartial[];
}

interface Props {
  jobs: JobInfo[];
  /** Studio uses a compact rail; Gallery keeps the default visual grid. */
  variant?: "grid" | "compact";
  /** Continue/edit a completed Gemini image result in the same interaction thread. */
  onContinue?: (job: JobInfo) => void;
  /** Refetch trigger fired after a job is cancelled (e.g. SWR mutate). When omitted,
   *  the Cancel control is hidden — used by read-only views like the Gallery. */
  onCancelled?: () => void;
  /** Id of the job currently streaming live events; its card gets the `live` overlay. */
  liveJobId?: string | null;
  /** Live progress/partials for the `liveJobId` job. */
  live?: JobLiveState | null;
  /** Gallery-only two-stage deletion controls. */
  trashMode?: boolean;
  onTrash?: (job: JobInfo) => void;
  onRestore?: (job: JobInfo) => void;
  onPermanentDelete?: (job: JobInfo) => void;
  busyJobId?: string | null;
  /** Bulk selection: completed cards get a checkbox and the selected ones a ring. */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (job: JobInfo) => void;
  /** Fired after the heart toggled on the server, so the page can refetch. */
  onFavoriteChange?: (job: JobInfo, favorited: boolean) => void;
  /** Opens the move-to-project picker for one generation (from its detail view). */
  onMove?: (job: JobInfo) => void;
  /** More pages exist; stepping past the last loaded item asks for the next one. */
  hasMore?: boolean;
  onLoadMore?: () => unknown;
  /** Gallery grid: a fixed column count instead of the responsive default. */
  columns?: number;
  /** Gallery grid: how much of each card fits at the chosen column count. */
  cardSize?: GalleryCardSize;
  /** Tapping a selectable card toggles it instead of opening the viewer. */
  selectMode?: boolean;
}

const noopFavoriteChange = () => undefined;

export function ResultGrid({
  jobs,
  variant = "grid",
  onContinue,
  onCancelled,
  liveJobId,
  live,
  trashMode = false,
  onTrash,
  onRestore,
  onPermanentDelete,
  busyJobId,
  selectedIds,
  onToggleSelect,
  onFavoriteChange,
  onMove,
  hasMore = false,
  onLoadMore,
  columns,
  cardSize = "full",
  selectMode = false,
}: Props) {
  const compact = variant === "compact";
  const navigate = useNavigate();
  const requestRecreate = useRecreateStore((state) => state.requestRecreate);
  const [detailId, setDetailId] = useState<string | null>(null);
  // Id of the item whose "next" is waiting for another page to arrive.
  const [pendingNextFrom, setPendingNextFrom] = useState<string | null>(null);
  const viewable = useMemo(() => viewerItems(jobs), [jobs]);
  const detailJob = detailId ? jobs.find((job) => job.id === detailId) ?? null : null;
  const viewIndex = detailId ? viewable.findIndex((job) => job.id === detailId) : -1;
  const lastViewIndex = useRef(-1);
  if (viewIndex >= 0) lastViewIndex.current = viewIndex;

  const openDetail = useCallback((job: JobInfo) => {
    lastViewIndex.current = -1;
    setPendingNextFrom(null);
    setDetailId(job.id);
  }, []);
  const closeDetail = useCallback(() => {
    setPendingNextFrom(null);
    setDetailId(null);
  }, []);

  // A viewed item that leaves the list (moved, trashed, filtered out) hands over
  // to the item now in its place, or closes the viewer when nothing is left.
  useEffect(() => {
    if (!detailId || detailJob) return;
    const fallback = viewable[Math.min(lastViewIndex.current, viewable.length - 1)];
    setPendingNextFrom(null);
    setDetailId(lastViewIndex.current >= 0 && fallback ? fallback.id : null);
  }, [detailId, detailJob, viewable]);

  // Finish a "next" that had to wait for the following page.
  useEffect(() => {
    if (!pendingNextFrom) return;
    const next = neighborIndex(viewable, pendingNextFrom, 1);
    if (next >= 0) {
      setPendingNextFrom(null);
      if (detailId === pendingNextFrom) setDetailId(viewable[next].id);
    } else if (!hasMore) {
      setPendingNextFrom(null);
    }
  }, [pendingNextFrom, viewable, hasMore, detailId]);

  const prevIndex = viewIndex > 0 ? viewIndex - 1 : -1;
  const nextIndex = viewIndex >= 0 && viewIndex < viewable.length - 1 ? viewIndex + 1 : -1;
  const canLoadNext = viewIndex >= 0 && nextIndex < 0 && hasMore && Boolean(onLoadMore);
  const goPrev = useMemo(
    () => (prevIndex >= 0 ? () => setDetailId(viewable[prevIndex].id) : undefined),
    [prevIndex, viewable]
  );
  const goNext = useMemo(() => {
    if (nextIndex >= 0) return () => setDetailId(viewable[nextIndex].id);
    if (canLoadNext && detailId && !pendingNextFrom) {
      return () => {
        setPendingNextFrom(detailId);
        void Promise.resolve(onLoadMore?.()).catch(() => setPendingNextFrom(null));
      };
    }
    return undefined;
  }, [nextIndex, viewable, canLoadNext, detailId, pendingNextFrom, onLoadMore]);

  // Warm the neighbours' signed URLs (and image bytes) so a step feels instant.
  const prevAsset = prevIndex >= 0 ? viewable[prevIndex].result.assets?.[0] : undefined;
  const nextAsset = nextIndex >= 0 ? viewable[nextIndex].result.assets?.[0] : undefined;
  const { url: prevUrl } = useAssetUrl(detailId ? prevAsset?.storage_key : null);
  const { url: nextUrl } = useAssetUrl(detailId ? nextAsset?.storage_key : null);
  useEffect(() => {
    for (const [asset, url] of [[prevAsset, prevUrl], [nextAsset, nextUrl]] as const) {
      if (asset?.type === "image" && url) new Image().src = url;
    }
  }, [prevAsset, prevUrl, nextAsset, nextUrl]);

  if (jobs.length === 0) {
    return (
      <EmptyState icon={Film} size={compact ? "sm" : "lg"} title="Your generations will appear here" />
    );
  }

  return (
    <div
      className={cn(
        compact
          ? "flex flex-col gap-2"
          : columns
            ? cn("grid items-start", cardSize === "tile" ? "gap-1 sm:gap-2" : "gap-2 sm:gap-3")
            : "grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
      )}
      style={!compact && columns ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } : undefined}
      data-columns={!compact && columns ? columns : undefined}
    >
      {jobs.map((job) => (
        <JobCard
          key={job.id}
          job={job}
          compact={compact}
          size={compact ? "full" : cardSize}
          selectMode={selectMode}
          onContinue={onContinue}
          onCancelled={onCancelled}
          trashMode={trashMode}
          onTrash={onTrash}
          onRestore={onRestore}
          onPermanentDelete={onPermanentDelete}
          busy={busyJobId === job.id}
          live={liveJobId && job.id === liveJobId ? live ?? null : null}
          selected={selectedIds?.has(job.id) ?? false}
          onToggleSelect={onToggleSelect}
          onFavoriteChange={onFavoriteChange}
          onOpenDetail={openDetail}
        />
      ))}
      <JobDetailDialog
        job={detailJob}
        open={detailJob !== null}
        onClose={closeDetail}
        onContinue={onContinue}
        onRecreate={
          detailJob
            ? () => {
                requestRecreate(detailJob);
                closeDetail();
                navigate("/");
              }
            : undefined
        }
        onMove={onMove && !trashMode && detailJob?.can_manage ? () => onMove(detailJob) : undefined}
        onFavoriteChange={trashMode ? undefined : onFavoriteChange ?? noopFavoriteChange}
        onTrash={onTrash && !trashMode && detailJob?.can_manage ? () => onTrash(detailJob) : undefined}
        position={viewIndex >= 0 ? { index: viewIndex, total: viewable.length, hasMore } : null}
        onPrev={viewIndex >= 0 ? goPrev : undefined}
        onNext={viewIndex >= 0 ? goNext : undefined}
        loadingNext={pendingNextFrom !== null && pendingNextFrom === detailId}
      />
    </div>
  );
}

function JobCard({
  job,
  compact,
  size,
  selectMode,
  onContinue,
  onCancelled,
  trashMode,
  onTrash,
  onRestore,
  onPermanentDelete,
  busy,
  live,
  selected,
  onToggleSelect,
  onFavoriteChange,
  onOpenDetail,
}: {
  job: JobInfo;
  compact: boolean;
  size: GalleryCardSize;
  selectMode: boolean;
  onContinue?: (job: JobInfo) => void;
  onCancelled?: () => void;
  trashMode: boolean;
  onTrash?: (job: JobInfo) => void;
  onRestore?: (job: JobInfo) => void;
  onPermanentDelete?: (job: JobInfo) => void;
  busy: boolean;
  live?: JobLiveState | null;
  selected: boolean;
  onToggleSelect?: (job: JobInfo) => void;
  onFavoriteChange?: (job: JobInfo, favorited: boolean) => void;
  onOpenDetail: (job: JobInfo) => void;
}) {
  const [imgError, setImgError] = useState(false);
  const selectable = Boolean(onToggleSelect) && !trashMode && isSelectable(job);
  const tile = size === "tile";
  // Medium and tile cards leave the per-item actions to the viewer.
  const slim = size !== "full";
  const toggleOnTap = selectMode && selectable;
  const activate = () => (toggleOnTap ? onToggleSelect?.(job) : onOpenDetail(job));
  const [favorited, setFavorited] = useState(Boolean(job.favorited_at));
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  // The server value wins whenever the list refetches.
  useEffect(() => {
    setFavorited(Boolean(job.favorited_at));
  }, [job.favorited_at]);
  const [cancelling, setCancelling] = useState(false);
  const { download, downloading, failed: downloadError } = useDownloadAsset();
  const [saveOpen, setSaveOpen] = useState(false);
  const [savedLibraryItem, setSavedLibraryItem] = useState<LibraryItemInfo | null>(null);
  const navigate = useNavigate();
  const requestRecreate = useRecreateStore((state) => state.requestRecreate);
  const requestReference = useRecreateStore((state) => state.requestReference);
  const isVideo = job.result.assets?.some((a) => a.type === "video");
  const isAudio = job.result.assets?.some((a) => a.type === "audio");
  const asset = job.result.assets?.[0];
  const isProfessionalMov = asset?.mime_type === "video/quicktime";
  const failedWithoutCharge =
    job.state === "failed" &&
    job.settled_charge_usd != null &&
    Number(job.settled_charge_usd) === 0;
  const cardRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  useEffect(() => {
    const card = cardRef.current;
    if (!card || nearViewport) return;
    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: "400px" });
    observer.observe(card);
    return () => observer.disconnect();
  }, [nearViewport]);

  // Defer the signed-URL exchange as well as the media download. Image asset
  // ids resolve to an existing thumbnail; detail/download still use the source.
  const previewEnabled = nearViewport;
  const previewId = previewEnabled && asset?.type === "image" ? asset.asset_id : null;
  const thumbnail = useAssetIdUrl(previewId);
  const original = useAssetUrl(previewEnabled && !previewId ? asset?.storage_key : null);
  // A video poster stands in for the clip, which then loads only when played.
  // Hold the player until the poster resolves so it never mounts without it.
  const posterKey = asset?.type === "video" ? asset.thumbnail_storage_key : null;
  const { url: posterUrl, error: posterError } = useAssetUrl(previewEnabled ? posterKey : null);
  const posterPending = Boolean(posterKey) && !posterUrl && !posterError;
  const resolved = previewId ? thumbnail : original;
  const mediaUrl = posterPending ? null : resolved.url;
  const mediaError = resolved.error;
  // Clear the failure flag whenever a fresh (re-presigned) URL arrives.
  // It was only ever set to true, so one expired URL left the card reading
  // "Preview unavailable" until a full page reload.
  useEffect(() => {
    setImgError(false);
  }, [mediaUrl]);
  const broken = imgError || !!mediaError;
  const ratioLabel = compact ? null : aspectBadgeLabel(asset, job.params);
  const probedDuration = asset?.metadata?.duration_sec;
  const durationSeconds =
    typeof probedDuration === "number" && Number.isFinite(probedDuration) ? probedDuration : null;

  const stateConfig = {
    queued: { icon: Loader2, label: "Queued", color: "text-studio-muted", spin: true },
    reserved: { icon: Loader2, label: "Reserved", color: "text-studio-muted", spin: true },
    dispatching: { icon: Loader2, label: "Dispatching", color: "text-studio-warning", spin: true },
    provider_pending: { icon: Loader2, label: "Processing", color: "text-studio-warning", spin: true },
    provider_processing: { icon: Loader2, label: "Processing", color: "text-studio-warning", spin: true },
    ingesting: { icon: Loader2, label: "Saving", color: "text-studio-warning", spin: true },
    reconciliation_required: { icon: AlertCircle, label: "Needs review", color: "text-studio-warning", spin: false },
    awaiting_price_approval: { icon: AlertCircle, label: "Price approval needed", color: "text-studio-warning", spin: false },
    completed: { icon: CheckCircle2, label: "Done", color: "text-studio-success", spin: false },
    failed: { icon: AlertCircle, label: "Failed", color: "text-studio-danger", spin: false },
    cancelled: { icon: AlertCircle, label: "Cancelled", color: "text-studio-muted", spin: false },
  } as const;

  const cfg = stateConfig[job.state as keyof typeof stateConfig] ?? stateConfig.queued;
  const StateIcon = cfg.icon;

  // Cancel is offered only on non-terminal states and only when the parent wired a
  // refetch handler. A refetch reconciles the UI regardless of cancel success (e.g. a
  // sync job that already dispatched rejects with 409 and simply keeps processing).
  const cancellable = !!onCancelled && CANCELLABLE_STATES.includes(job.state as JobState);

  // Live streaming overlay — only while the job is genuinely in progress
  // (never for completed/failed/cancelled, which render their own terminal UI).
  const inProgress = !isTerminalState(job.state);
  const lastPartial = live?.partials.length ? live.partials[live.partials.length - 1] : null;
  const showPreview = inProgress && !!lastPartial;
  const pct = inProgress && typeof live?.pct === "number" ? Math.max(0, Math.min(100, live.pct)) : null;
  const progressMessage = inProgress ? live?.message : undefined;

  async function handleCancel() {
    if (cancelling) return;
    if (!window.confirm("Cancel this generation? Any reserved USD balance will be refunded.")) return;
    setCancelling(true);
    try {
      await cancelJob(job.id);
    } catch {
      // Swallow — the refetch below reconciles to the true server state.
    } finally {
      setCancelling(false);
      onCancelled?.();
    }
  }

  function handleRecreate() {
    requestRecreate(job);
    navigate("/");
  }

  async function toggleFavorite() {
    if (favoriteBusy || !job.can_manage) return;
    const next = !favorited;
    setFavorited(next);
    setFavoriteBusy(true);
    try {
      await (next ? favoriteJob(job.id) : unfavoriteJob(job.id));
      onFavoriteChange?.(job, next);
    } catch {
      setFavorited(!next);
    } finally {
      setFavoriteBusy(false);
    }
  }

  async function handleDownload() {
    if (!asset || downloading) return;
    const filename = buildDownloadFilename(job.model_key, job.id, 0, asset.mime_type);
    await download({ filename, resolveUrl: () => presignAsset(asset.storage_key, { download: true, filename }) });
  }

  return (
    <div
      ref={cardRef}
      role="group"
      aria-label={`${job.model_key} generation — ${cfg.label}`}
      className={cn(
        "hover-lift group relative cursor-pointer overflow-hidden rounded-xl border border-studio-border bg-studio-card shadow-sm transition-[border-color,background-color,box-shadow] hover:border-studio-border-light hover:bg-studio-card-hover",
        compact && "flex min-h-[84px]",
        tile && "rounded-lg",
        selected && "border-studio-accent ring-2 ring-studio-accent/60 hover:border-studio-accent"
      )}
      data-selected={selected || undefined}
      data-size={size}
      onClick={activate}
    >
      {isTerminalState(job.state) && (
        // Transparent to the mouse so hover-to-play still reaches the video underneath.
        <button
          type="button"
          onClick={activate}
          aria-label={toggleOnTap ? `${selected ? "Deselect" : "Select"} — ${job.model_key} generation` : `Open details — ${job.model_key} generation`}
          className="pointer-events-none absolute inset-0 z-10 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-studio-accent"
        />
      )}

      {selectable && (!tile || selectMode || selected) && (
        <button
          type="button"
          role="checkbox"
          aria-checked={selected}
          aria-label={selected ? "Deselect this generation" : "Select this generation"}
          title={selected ? "Deselect" : "Select"}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect?.(job);
          }}
          className={cn(
            "absolute z-20 flex items-center justify-center rounded-full border-2 text-white backdrop-blur transition-[background-color,border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white",
            compact || tile ? "left-1 top-1 h-5 w-5" : "left-2 top-2 h-6 w-6",
            selected ? "border-white bg-studio-accent" : "border-white/80 bg-black/45 opacity-80 hover:bg-black/70 hover:opacity-100 group-hover:opacity-100"
          )}
        >
          <Check className={cn("h-3.5 w-3.5", !selected && "opacity-0")} />
        </button>
      )}

      {/* Preview */}
      <div
        className={cn(
          "relative bg-studio-bg",
          compact ? "h-[84px] w-[84px] shrink-0 self-stretch" : slim ? "aspect-square w-full" : "aspect-[4/3] w-full"
        )}
      >
        {job.state === "completed" && asset ? (
          mediaUrl && !broken ? (
            isVideo && isProfessionalMov ? (
              <div
                className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-studio-card to-studio-bg px-5 text-center"
                onClick={(event) => event.stopPropagation()}
              >
                <Film className="h-8 w-8 text-studio-accent" />
                <div>
                  <p className="text-xs font-semibold text-studio-ink">Professional MOV output</p>
                  <p className="mt-1 text-[10px] leading-4 text-studio-muted">This H.264 4:4:4 / PCM file may not preview in the browser. Download it for a compatible editor or player.</p>
                </div>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleDownload();
                  }}
                  disabled={downloading}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-semibold disabled:opacity-60",
                    downloadError ? "bg-studio-danger text-studio-danger-fg" : "bg-studio-accent text-studio-accent-fg"
                  )}
                >
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : downloadError ? <AlertCircle className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
                  {downloadError ? "Download failed — tap to retry" : "Download MOV"}
                </button>
                <span role="status" aria-live="polite" className="sr-only">{downloadError ? "Download failed. Please try again." : ""}</span>
              </div>
            ) : (
              <AssetMedia
                type={asset.type}
                url={mediaUrl}
                poster={posterUrl}
                alt={(job.params.prompt as string) || "generation"}
                fit={isAudio ? "contain" : "cover"}
                playOnHover={!compact}
                lazyPreload
                durationSeconds={durationSeconds}
                onImageError={() => setImgError(true)}
                className={isAudio ? undefined : "bg-studio-bg"}
              />
            )
          ) : broken ? (
            <div className="flex h-full flex-col items-center justify-center text-studio-muted">
              <ImageOff className="h-7 w-7 text-studio-muted/50" />
              <span className="mt-2 text-[11px]">Preview unavailable</span>
            </div>
          ) : (
            // Resolving the signed media URL.
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-studio-muted/50" />
            </div>
          )
        ) : (
          <div className="relative flex h-full flex-col items-center justify-center">
            {/* Streaming partial preview (last frame fills the cell). */}
            {showPreview && (
              <>
                <img
                  src={lastPartial!.dataUri}
                  alt="streaming preview"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span className="absolute left-2 top-2 z-10 rounded-md bg-studio-accent/80 px-2 py-1 text-[10px] font-medium text-studio-accent-fg backdrop-blur">
                  Preview
                </span>
              </>
            )}
            {/* Status sits above the preview, with a scrim for legibility. */}
            <div
              className={cn(
                "relative z-10 flex flex-col items-center justify-center",
                showPreview && "rounded-xl bg-black/45 px-4 py-3 backdrop-blur-sm"
              )}
            >
              <StateIcon className={cn(compact || tile ? "h-5 w-5" : "h-8 w-8", cfg.color, cfg.spin && "animate-spin")} />
              <span className={cn(compact || tile ? "mt-1 text-[9px]" : "mt-2 text-xs", cfg.color)}>{cfg.label}</span>
              {!tile && job.state === "failed" && job.error && (
                <span
                  title={job.error.suggested_action}
                  className={cn("mt-1 max-w-[80%] text-center text-studio-danger/80", compact ? "line-clamp-1 text-[8px]" : "text-[10px]")}
                >
                  {job.error.message}
                </span>
              )}
              {inProgress && !tile && (
                <div className="mt-3 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenDetail(job);
                    }}
                    aria-label="Details"
                    title="View generation details"
                    className="inline-flex items-center gap-1.5 rounded-md border border-studio-border bg-studio-bg/70 px-2.5 py-1 text-[11px] font-medium text-studio-muted backdrop-blur transition-colors hover:border-studio-accent/50 hover:text-studio-ink"
                  >
                    <Info className="h-3 w-3" />
                    Details
                  </button>
                  {cancellable && (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleCancel();
                      }}
                      disabled={cancelling}
                      className="inline-flex items-center gap-1.5 rounded-md border border-studio-border bg-studio-bg/70 px-2.5 py-1 text-[11px] font-medium text-studio-muted backdrop-blur transition-colors hover:border-studio-danger/50 hover:text-studio-danger disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {cancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                      {cancelling ? "Cancelling…" : "Cancel"}
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Progress bar + message (when the stream reports pct). */}
            {pct != null && (
              <div className="absolute inset-x-0 bottom-0 z-10 px-3 pb-3">
                <div className="flex items-center justify-between text-[10px] font-medium text-white/85">
                  <span className="truncate">{progressMessage ?? "Processing…"}</span>
                  <span className="ml-2 shrink-0 tabular-nums">{Math.round(pct)}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-black/40">
                  <div
                    className="h-full rounded-full bg-studio-accent transition-all duration-300"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* State badge */}
        {job.state !== "completed" && !tile && (
          <div className="absolute left-2 top-2 rounded-md bg-black/60 px-2 py-1 text-[10px] font-medium backdrop-blur">
            <span className={cfg.color}>{cfg.label}</span>
          </div>
        )}
        {job.state === "completed" && ratioLabel && !slim && (
          <span className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-black/70 px-1.5 py-0.5 font-mono text-[10px] tabular-nums text-white">
            {ratioLabel}
          </span>
        )}
        {trashMode && !tile && (
          <div className="absolute right-2 top-2 flex items-center gap-1.5 rounded-md bg-black/65 px-2 py-1 text-[10px] font-medium text-white backdrop-blur">
            <Trash2 className="h-3 w-3" />
            Trash
          </div>
        )}
        {!trashMode && job.state === "completed" && (favorited || (job.can_manage && !tile && !selectMode)) && (
          <button
            type="button"
            aria-pressed={favorited}
            aria-label={favorited ? "Remove from favorites" : "Add to favorites"}
            title={!job.can_manage ? "Favorited" : favorited ? "Remove from favorites" : "Add to favorites"}
            disabled={!job.can_manage || favoriteBusy}
            onClick={(event) => {
              event.stopPropagation();
              void toggleFavorite();
            }}
            className={cn(
              "touch-actions absolute z-20 flex items-center justify-center rounded-full backdrop-blur transition-[opacity,background-color,transform] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-default",
              compact || tile ? "bottom-1 right-1 h-6 w-6" : "bottom-2 right-2 h-9 w-9",
              favorited
                ? "bg-black/60 text-studio-danger opacity-100"
                : "bg-black/45 text-white opacity-0 hover:bg-black/70 group-hover:opacity-100 group-focus-within:opacity-100"
            )}
          >
            <Heart className={cn(tile ? "h-3 w-3" : "h-4 w-4", favorited && "fill-current")} />
          </button>
        )}
      </div>

      {/* Info */}
      {tile ? (
        <PriceApprovalControl job={job} />
      ) : slim ? (
        <div className="px-2.5 py-2">
          <p className="truncate text-[11px] leading-4 text-studio-muted">{job.params.prompt as string || "—"}</p>
          <PriceApprovalControl job={job} />
        </div>
      ) : (
      <div className={cn("p-3", compact && "min-w-0 flex-1 py-2.5 pr-9")}>
        <p className={cn("text-xs text-studio-muted", compact ? "line-clamp-2 leading-4" : "line-clamp-2 min-h-10 leading-5")}>
          {job.params.prompt as string || "—"}
        </p>
        {job.state === "failed" && job.error && (
          <div className={cn("mt-2 rounded-md border border-studio-danger/20 bg-studio-danger/5 px-2 py-1.5", compact && "mt-1 py-1")}>
            {!compact && job.error.suggested_action && (
              <p className="line-clamp-2 text-[10px] leading-4 text-studio-muted">
                {job.error.suggested_action}
              </p>
            )}
            <p
              className={cn("font-mono text-studio-danger/75", compact ? "truncate text-[8px]" : "mt-1 line-clamp-2 break-all text-[9px]")}
              title={job.error.provider_code ? `Provider: ${job.error.provider_code}` : undefined}
            >
              Error: {job.error.code}
              {!compact && job.error.provider_code && ` · Provider: ${job.error.provider_code}`}
            </p>
          </div>
        )}
        <div className={cn("mt-1.5 flex items-center justify-between text-studio-muted/70", compact ? "gap-2 text-[9px]" : "text-[10px]")}>
          <span className="truncate">{job.model_key}</span>
          {job.user_id && <span className="truncate text-studio-faint" title={`Owner: ${job.owner_name || job.user_id}`}>Owner: {job.owner_name || job.user_id.slice(0, 8)}</span>}
          {job.settled_charge_usd != null && (
            <span className={failedWithoutCharge ? "text-studio-success" : undefined}>
              {failedWithoutCharge
                ? "No charge"
                : formatUsd(job.settled_charge_usd)}
            </span>
          )}
          {job.billing_status === "reconciliation_required" && <span>Billing under review</span>}
        </div>
        <PriceApprovalControl job={job} />
      </div>
      )}

      {trashMode && job.can_manage && (
        <div
          className={cn("grid grid-cols-2 border-t border-studio-border", tile ? "gap-1 p-1" : "gap-2 px-3 py-3")}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => onRestore?.(job)}
            disabled={busy}
            aria-label="Restore"
            title="Restore"
            className={cn(tile && "!h-8 !px-0", "inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-studio-border bg-studio-panel px-3 text-xs font-semibold text-studio-ink transition-[background-color,border-color,transform] hover:border-studio-border-light hover:bg-studio-card-hover active:scale-[0.96] focus-visible:outline-none focus-visible:shadow-glow-sm sm:h-10")}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {!tile && (busy ? "Restoring…" : "Restore")}
          </button>
          <button
            type="button"
            onClick={() => onPermanentDelete?.(job)}
            disabled={busy}
            aria-label="Delete forever"
            title="Delete forever"
            className={cn(tile && "!h-8 !px-0", "inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-studio-danger/90 px-3 text-xs font-semibold text-studio-danger-fg transition-[background-color,transform] hover:bg-studio-danger active:scale-[0.96] focus-visible:outline-none focus-visible:shadow-glow-sm sm:h-10")}
          >
            <Trash2 className="h-4 w-4" />
            {!tile && "Delete forever"}
          </button>
        </div>
      )}

      {/* Hover actions */}
      {!trashMode && !slim && !selectMode && isTerminalState(job.state) && (
        <div
          className={cn(
            "touch-actions absolute z-20 flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100",
            compact ? "right-1 top-1 gap-1" : "right-2 top-2 gap-1.5",
            // Keep the hover actions clear of the selection checkbox; extra buttons wrap under.
            selectable && "left-10 flex-wrap justify-end"
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => onOpenDetail(job)}
            aria-label="Details"
            title="View generation details"
            className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-[background-color,transform] hover:bg-studio-accent/80 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={handleRecreate}
            aria-label="Recreate"
            title="Restore these settings in Studio"
            className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-[background-color,transform] hover:bg-studio-accent/80 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}
          >
            <Repeat2 className="h-3.5 w-3.5" />
          </button>
          {job.state === "completed" && mediaUrl && !broken && (
            <>
              {asset?.type === "image" && asset.asset_id && <button type="button" aria-label="Use as video reference" title="Use as video reference"
                onClick={() => { requestReference({ asset_id: asset.asset_id!, type: "image", mime: asset.mime_type ?? "application/octet-stream", storage_key: asset.storage_key }); navigate("/"); }}
                className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur hover:bg-studio-accent/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}><ImagePlus className="h-3.5 w-3.5" /></button>}
              <button
                type="button"
                onClick={() => setSaveOpen(true)}
                aria-label={savedLibraryItem ? "Saved to Library" : "Save to Library"}
                title={savedLibraryItem ? "Edit Library entry" : "Save to Library"}
                className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-[background-color,transform] hover:bg-studio-accent/80 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}
              >
                {savedLibraryItem ? <BookmarkCheck className="h-3.5 w-3.5" /> : <BookmarkPlus className="h-3.5 w-3.5" />}
              </button>
              {/* Continue/edit — only for Gemini image results that expose an interaction id. */}
              {onContinue && job.result.interaction_id && !isVideo && !isAudio && (
                <button
                  type="button"
                  onClick={() => onContinue(job)}
                  aria-label="Continue editing"
                  title="Continue editing in this thread"
                  className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-[background-color,transform] hover:bg-studio-accent/80 active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}
                >
                  <Wand2 className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => void handleDownload()}
                disabled={downloading}
                aria-label={downloadError ? "Download failed — click to retry" : "Download"}
                title={downloadError ? "Download failed — click to retry" : "Download"}
                className={cn(
                  "inline-flex items-center justify-center rounded-lg text-white backdrop-blur transition-[background-color,transform] active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60",
                  downloadError ? "bg-studio-danger/90 hover:bg-studio-danger" : "bg-black/60 hover:bg-black/80",
                  compact ? "h-8 w-8" : "h-10 w-10"
                )}
              >
                {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : downloadError ? <AlertCircle className="h-3.5 w-3.5" /> : <Download className="h-3.5 w-3.5" />}
              </button>
              <span role="status" aria-live="polite" className="sr-only">{downloadError ? "Download failed. Please try again." : ""}</span>
            </>
          )}
          {onTrash && job.can_manage && (
            <button
              type="button"
              onClick={() => onTrash(job)}
              aria-label="Move to Trash"
              title="Move to Trash"
              className={cn("inline-flex items-center justify-center rounded-lg bg-black/60 text-white backdrop-blur transition-[background-color,transform] hover:bg-studio-danger active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white", compact ? "h-8 w-8" : "h-10 w-10")}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      <SaveToLibraryDialog
        job={job}
        open={saveOpen}
        initialItem={savedLibraryItem}
        onClose={() => setSaveOpen(false)}
        onSaved={setSavedLibraryItem}
      />
    </div>
  );
}
